import { Controller, useFormContext } from 'react-hook-form';
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import { Email } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import { Modal } from '../../../components/modals/Modal';
import type { ChangeEmailRequestValues } from '../schemas/changeEmailSchema';

export type ChangeEmailRequestModalProps = {
  open: boolean;
  currentEmail: string;
  loading: boolean;
  onSubmit: (values: ChangeEmailRequestValues) => void;
  onCancel: () => void;
};

export function ChangeEmailRequestModal({ open, currentEmail, loading, onSubmit, onCancel }: ChangeEmailRequestModalProps) {
  const { control, handleSubmit } = useFormContext<ChangeEmailRequestValues>();

  return (
    <Modal
      open={open}
      onClose={loading ? () => {} : onCancel}
      sx={{
        '& > .MuiBox-root': {
          width: '100%',
          maxWidth: 440,
          borderRadius: '16px',
        },
      }}
    >
      <Modal.Header>
        <Box
          sx={{
            bgcolor: 'primary.main',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography component="h2" sx={{ color: '#fff', fontWeight: 600, fontSize: '18px' }}>
            Change E-mail Address (User ID)
          </Typography>
          <IconButton onClick={onCancel} disabled={loading} size="small" sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Modal.Header>

      <Box component="form" onSubmit={handleSubmit((values) => void onSubmit(values))}>
        <Modal.Body>
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, bgcolor: 'background.paper' }}>
            <Box
              sx={{
                p: 2,
                borderRadius: '12px',
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Current email
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: '15px', wordBreak: 'break-all' }}>{currentEmail}</Typography>
            </Box>

            <Controller
              name="newEmail"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  id="newEmail"
                  fullWidth
                  size="small"
                  type="email"
                  label="New Email Address"
                  placeholder="Enter new email address"
                  autoFocus
                  autoComplete="off"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    // The app theme forces TextField input text down to 12px while
                    // helper/error text (caption variant) sits at 14px — backwards
                    // hierarchy where the error reads larger than what you typed.
                    '& .MuiInputBase-input': { fontSize: '15px' },
                    '& .MuiFormHelperText-root': { fontSize: '13px', lineHeight: 1.4, mt: 0.5 },
                  }}
                />
              )}
            />
          </Box>
        </Modal.Body>

        <Modal.Footer>
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor: 'grey.50',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
            }}
          >
            <Button variant="outlined" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              endIcon={<ArrowForwardIcon />}
              sx={{
                bgcolor: '#EE5B2B',
                '&:hover': { bgcolor: '#EE5B2B' },
              }}
            >
              Continue
            </LoadingButton>
          </Box>
        </Modal.Footer>
      </Box>
    </Modal>
  );
}
